import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import EnterpriseLearningPathContext from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import EnterpriseLearningPathPageQuery from 'bundles/enterprise-learning-path-description-page/queries/EnterpriseLearningPathPageQuery.graphql';
import type {
  EnterpriseLearningPathPageQuery as EnterpriseLearningPathPageQueryType,
  EnterpriseLearningPathPageQueryVariables,
} from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageQuery';
import { useNaptime } from 'bundles/naptimejs';
import ProgramCurriculumsV1 from 'bundles/naptimejs/resources/programCurriculums.v1';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

type PropsToComponent = {
  children: React.ReactNode;
};

const EnterpriseLearningPathDataProvider = ({ children }: PropsToComponent) => {
  const program = useProgram();
  const { badgeTemplateSlug } = useParams();
  const programId = program?.id ?? '';

  const { data, loading, error } = useQuery<
    EnterpriseLearningPathPageQueryType,
    EnterpriseLearningPathPageQueryVariables
  >(EnterpriseLearningPathPageQuery, {
    skip: !programId,
    // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    variables: { programId, badgeTemplateSlug },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const curriculumId = program?.metadata.curriculumId;
  const {
    data: { curriculum },
  } = useNaptime<{ curriculum: ProgramCurriculumsV1 }>(
    () => ({
      curriculum: curriculumId
        ? ProgramCurriculumsV1.get(curriculumId, {
            fields: ['definition'],
          })
        : undefined,
    }),
    [curriculumId]
  );

  const enterpriseLearningPathPageData = data?.EnterpriseBadge?.findByProgramAndBadgeTemplateSlug;
  const matchingBadge = (_: { badgeTemplateId?: string }) =>
    _.badgeTemplateId === enterpriseLearningPathPageData?.enterpriseBadge?.id;
  const learningPath =
    curriculum?.definition?.coreTracks?.find(matchingBadge) ??
    curriculum?.definition?.electiveTracks?.find(matchingBadge);

  return (
    <EnterpriseLearningPathContext.Provider
      value={{
        learningPathPage: enterpriseLearningPathPageData,
        learningPathName: learningPath?.title,
        isLPDataLoading: loading,
        collectionId: learningPath?.id,
        error,
      }}
    >
      {children}
    </EnterpriseLearningPathContext.Provider>
  );
};

export default EnterpriseLearningPathDataProvider;
